import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useMemo, useState } from 'react';
import type { VisualDxDiagnosticFlowSelections } from '../interactive-widget/submission';
import {
  STEP_ORDER,
  TERMINAL_STEP_CTA_LABEL,
  type AgeBand,
  type BodyAspect,
  type Morphology,
  type OnsetSpeed,
  type Region,
  type Step,
} from './VisualDxDiagnosticFlow.constants';
import {
  AgeStep,
  MorphologyStep,
  OnsetStep,
  RegionStep,
} from './VisualDxDiagnosticFlow.parts';
import { ReviewSummary } from './VisualDxDiagnosticFlow.review';
import {
  attributionClass,
  bodyClass,
  cardClass,
  footerClass,
  footerRightGroupClass,
  progressBarFillClass,
  progressBarTrackClass,
} from './VisualDxDiagnosticFlow.styles';

/**
 * Heidi-native interactive VisualDx diagnostic flow.
 *
 * Renders the demo's four-screen guided differential UI inline as a real
 * React component. Mounted by ``InteractiveWidgetBlock`` for the
 * ``visualdx_diagnostic_flow`` widget kind whenever the LLM calls
 * ``start_visualdx_diagnostic_flow`` and the BE acks with the matching
 * ``interactive_widget`` chunk.
 *
 * Step order (matches the canonical Loom — see ``STEP_ORDER``):
 *   1. region        — Front/Back tab + 12-region multi-select grid
 *   2. age           — age band chip + exact-age input (either is fine)
 *   3. onset         — single-select onset-speed cards with line icons
 *   4. morphology    — wrap of finding pills, multi-select. Terminal
 *                      step; CTA reads "Build differential" and submits.
 *
 * On submit (or "Skip to the Results"), calls ``onSubmit`` with the typed
 * selection payload + a ``skipped`` flag. The parent (``InteractiveWidgetBlock``
 * -> ``Chat.tsx``) formats it into a natural-language message and sends
 * it as the next user turn; the LLM then triggers the
 * ``render_visualdx_ranked_differential`` tool which mounts the numbered
 * differential card list as a separate widget kind.
 *
 * Once submitted, the input form is replaced with a ``ReviewSummary``
 * card so the user sees what was sent while the differential is being
 * generated.
 *
 * i18n note: per PR #9851 review, this surface is intentionally
 * English-only for the EVI-1349 MVP (one geo, dermatology-only beta).
 * Strings will be migrated to react-intl + Crowdin in a follow-up
 * ticket once the surface is GA across markets.
 */

interface VisualDxDiagnosticFlowProps {
  /** Called once when the user submits or skips; rejection leaves the form retryable. */
  onSubmit?: (selections: VisualDxDiagnosticFlowSelections) => void | Promise<void>;
  /** Rehydrates the submitted summary after the child-run wait resolves. */
  submittedSelections?: VisualDxDiagnosticFlowSelections;
}

const ATTRIBUTION_TEXT = 'Powered by VisualDx';
const ATTRIBUTION_LIBRARY_TEXT = 'Library updated Oct 2025';

export const VisualDxDiagnosticFlow = ({
  onSubmit,
  submittedSelections,
}: VisualDxDiagnosticFlowProps): JSX.Element => {
  const [step, setStep] = useState<Step>('region');
  const [bodyAspect, setBodyAspect] = useState<BodyAspect>(
    submittedSelections?.bodyAspect ?? 'front'
  );
  const [regions, setRegions] = useState<Set<Region>>(
    () => new Set(submittedSelections?.regions ?? [])
  );
  const [morphology, setMorphology] = useState<Set<Morphology>>(
    () => new Set(submittedSelections?.morphology ?? [])
  );
  const [ageBand, setAgeBand] = useState<AgeBand | null>(
    submittedSelections?.age && 'band' in submittedSelections.age
      ? submittedSelections.age.band
      : null
  );
  const [exactYears, setExactYears] = useState<string>(
    submittedSelections?.age && 'exactYears' in submittedSelections.age
      ? String(submittedSelections.age.exactYears)
      : ''
  );
  const [onset, setOnset] = useState<OnsetSpeed | null>(
    submittedSelections?.onset ?? null
  );
  const [submitted, setSubmitted] = useState(Boolean(submittedSelections));
  const [submittedSkipped, setSubmittedSkipped] = useState(
    submittedSelections?.skipped ?? false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stepIndex = STEP_ORDER.indexOf(step);
  const totalSteps = STEP_ORDER.length;
  const progressPct = ((stepIndex + 1) / totalSteps) * 100;
  const isTerminalStep = stepIndex === totalSteps - 1;

  const canAdvance = useMemo(() => {
    if (step === 'region') return regions.size > 0;
    if (step === 'age') return ageBand !== null || exactYears !== '';
    if (step === 'onset') return onset !== null;
    if (step === 'morphology') return morphology.size > 0;
    return true;
  }, [step, regions, morphology, ageBand, exactYears, onset]);

  const buildSelections = (skipped: boolean): VisualDxDiagnosticFlowSelections => ({
    bodyAspect,
    regions: Array.from(regions),
    morphology: Array.from(morphology),
    age:
      exactYears !== ''
        ? { exactYears: Number(exactYears) }
        : ageBand
          ? { band: ageBand }
          : null,
    onset,
    skipped,
  });

  const dispatchSubmit = async (skipped: boolean) => {
    if (!onSubmit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(buildSelections(skipped));
      setSubmittedSkipped(skipped);
      setSubmitted(true);
    } catch {
      // The wait remains pending, so keeping the form open is the retry affordance.
    } finally {
      setIsSubmitting(false);
    }
  };

  const goNext = () => {
    if (!canAdvance) return;
    const next = STEP_ORDER[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEP_ORDER[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const reset = () => {
    setStep('region');
    setBodyAspect('front');
    setRegions(new Set());
    setMorphology(new Set());
    setAgeBand(null);
    setExactYears('');
    setOnset(null);
    setSubmitted(false);
    setSubmittedSkipped(false);
  };

  const reviewProps = {
    bodyAspect,
    regions: Array.from(regions),
    morphology: Array.from(morphology),
    ageBand,
    exactYears,
    onset,
  } as const;

  return (
    <div className={cardClass}>
      <div className={progressBarTrackClass}>
        <div
          className={progressBarFillClass}
          // Width is the only dynamic property — every other styling
          // hook uses semantic tokens via className.
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <main className={bodyClass}>
        {submitted ? (
          <ReviewSummary
            {...reviewProps}
            onReset={onSubmit ? reset : undefined}
            skipped={submittedSkipped}
          />
        ) : step === 'region' ? (
          <RegionStep
            bodyAspect={bodyAspect}
            selected={regions}
            onAspectChange={setBodyAspect}
            onToggle={(id) => {
              setRegions((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
          />
        ) : step === 'age' ? (
          <AgeStep
            band={ageBand}
            exactYears={exactYears}
            onBandChange={(b) => {
              setAgeBand(b);
              setExactYears('');
            }}
            onExactYearsChange={(v) => {
              setExactYears(v);
              if (v !== '') setAgeBand(null);
            }}
          />
        ) : step === 'onset' ? (
          <OnsetStep selected={onset} onSelect={setOnset} />
        ) : (
          <MorphologyStep
            selected={morphology}
            onToggle={(id) => {
              setMorphology((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
          />
        )}
      </main>

      {!submitted && (
        <footer className={footerClass}>
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            disabled={stepIndex === 0}
            className={stepIndex === 0 ? 'invisible' : undefined}
          >
            Back
          </Button>
          <div className={footerRightGroupClass}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void dispatchSubmit(true)}
              disabled={isSubmitting}
            >
              Skip to the Results
            </Button>
            {isTerminalStep ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => void dispatchSubmit(false)}
                disabled={!canAdvance || isSubmitting}
              >
                {TERMINAL_STEP_CTA_LABEL}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={goNext}
                disabled={!canAdvance || isSubmitting}
              >
                Next
              </Button>
            )}
          </div>
        </footer>
      )}

      <div className={attributionClass}>
        <TypographyCaption className="text-text-secondary">
          {ATTRIBUTION_TEXT}
        </TypographyCaption>
        <TypographyCaption className="text-text-secondary">
          {ATTRIBUTION_LIBRARY_TEXT}
        </TypographyCaption>
      </div>
    </div>
  );
};
